import { Ionicons } from '@expo/vector-icons';
import * as FileSystem from 'expo-file-system/legacy';
import * as ImagePicker from 'expo-image-picker';
import * as Location from 'expo-location';
import { router } from 'expo-router';
import React, { useState, useCallback } from 'react';
import { View, Alert, TouchableOpacity, Image, Platform } from 'react-native';

// Note: We now use File objects consistently across all platforms

import { useAttachmentImage } from '@/components/attachments/use-attachment-image';
import { useData } from '@/components/data-provider';
import { AssociationsSearchModal } from '@/components/search/associations-search-modal';
import { Button, Text, Card, TextInput, useTheme, KeyboardAwareScrollView } from '@/components/ui';
import { useApi } from '@/hooks/use-api';
import { useClosestAssociationWithinRadius } from '@/hooks/use-closest-association-within-radius';
import { useLocation } from '@/hooks/use-location';
import { useLocationPermission } from '@/hooks/use-location-permission';
import { useSubmit } from '@/hooks/use-submit';
import { dataService } from '@/services/data-service';
import { createTypedFormDataFromObject } from '@/types/form-data';
import { AttachmentType, Association, InspectionReport, Attachment } from '@/types/models';
import { nativeUploadFormData } from '@/utils/native-form-file';

interface InspectionPhoto {
	id: string;
	file?: { uri: string; name: string; type: string; size: number }; // File object for new photos
	uri?: string; // URI for existing photos
	comment: string;
	fileName: string;
	timestamp: Date;
	isExisting?: boolean; // Track if this photo already exists in the database
	attachmentId?: string; // Store the attachment ID for existing photos
	attachment?: Attachment; // Store the full attachment for existing photos
}

// Component to handle image loading for inspection photos
const InspectionPhotoImage: React.FC<{ photo: InspectionPhoto }> = ({ photo }) => {
	// Always call the hook, but only use it for existing photos
	const { data: imageUri, loading, error } = useAttachmentImage(photo.attachment || null);

	// For new photos, use the file URI directly
	if (photo.file?.uri) {
		return (
			<Image
				source={{ uri: photo.file.uri }}
				style={{ width: '100%', height: '100%' }}
				resizeMode="cover"
			/>
		);
	}

	// For existing photos, use the attachment image hook
	if (loading) {
		return (
			<View style={{ width: '100%', height: '100%', backgroundColor: '#f0f0f0', justifyContent: 'center', alignItems: 'center' }}>
				<Text>Loading...</Text>
			</View>
		);
	}

	if (error || !imageUri) {
		return (
			<View style={{ width: '100%', height: '100%', backgroundColor: '#f0f0f0', justifyContent: 'center', alignItems: 'center' }}>
				<Text>Error</Text>
			</View>
		);
	}

	return (
		<Image
			source={{ uri: imageUri }}
			style={{ width: '100%', height: '100%' }}
			resizeMode="cover"
		/>
	);
};


interface InspectionFormProps {
	associationId?: string;
	onSuccess?: (inspectionReport: InspectionReport) => void;
	readOnly?: boolean;
	editMode?: boolean;
	inspectionData?: InspectionReport;
}

export const InspectionForm: React.FC<InspectionFormProps> = ({
	associationId,
	onSuccess,
	readOnly = false,
	editMode = false,
	inspectionData,
}) => {
	const theme = useTheme();
	const { user, fetchApi } = useApi();
	const { location, loading, error } = useLocation();
	const { hasPermission: hasLocationPermission } = useLocationPermission();
	const { isSubmitting, run } = useSubmit();
	const { refetchAll } = useData();

	// Determine if user can edit this inspection
	const canEdit = !readOnly && (editMode || !inspectionData) && user && inspectionData && user.id === inspectionData.userId;
	const isReadOnly = readOnly && !canEdit;


	const [photos, setPhotos] = useState<InspectionPhoto[]>([]);
	const [deletedPhotoIds, setDeletedPhotoIds] = useState<string[]>([]); // Track photos to delete
	const [title, setTitle] = useState(inspectionData?.title || '');
	const [selectedAssociation, setSelectedAssociation] = useState<Association | null>(null);
	const [manualAssociation, setManualAssociation] = useState<Association | null>(null);
	const [showAssociationModal, setShowAssociationModal] = useState(false);

	// Auto-detect closest association based on location
	const closestAssociation = useClosestAssociationWithinRadius(
		location?.latitude,
		location?.longitude,
	);

	// Debug logging
	React.useEffect(() => {
		// console.log('Location state:', { location, loading, error });
		// console.log('Closest association:', closestAssociation);
		// console.log('Selected association:', selectedAssociation);
	}, [location, loading, error, closestAssociation, selectedAssociation]);

	// Set the closest association as default if no associationId provided
	React.useEffect(() => {
		if (!associationId && closestAssociation?.association && !manualAssociation) {
			// console.log('Setting selected association:', closestAssociation.association);
			setSelectedAssociation(closestAssociation.association);
			// Auto-populate title with Association Name - Month/YYYY
			if (!title) {
				const now = new Date();
				const monthYear = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
				setTitle(`${closestAssociation.association.name} - ${monthYear}`);
			}
		}
	}, [associationId, closestAssociation, manualAssociation, title]);

	// Update association coordinates if they're missing
	const updateAssociationCoordinates = useCallback(async (association: Association) => {
		if (!location || (association.latitude && association.longitude)) {
			return association; // No location or already has coordinates
		}

		try {
			const updatedAssociation = await dataService.updateAssociation(association.id, {
				lat: location.latitude,
				lng: location.longitude,
			});
			return updatedAssociation;
		} catch (error) {
			console.error('Error updating association coordinates:', error);
			Alert.alert('Warning', 'Failed to update association location coordinates');
			return association; // Return original if update fails
		}
	}, [location]);

	// Handle manual association selection
	const handleManualAssociationSelect = useCallback(async (association: Association | null) => {
		setManualAssociation(association);
		if (association) {
			// Update coordinates if needed
			const updatedAssociation = await updateAssociationCoordinates(association);
			setSelectedAssociation(updatedAssociation);
			// Auto-populate title with Association Name - Month/YYYY
			if (!title) {
				const now = new Date();
				const monthYear = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
				setTitle(`${updatedAssociation.name} - ${monthYear}`);
			}
		} else {
			setSelectedAssociation(null);
		}
		setShowAssociationModal(false);
	}, [updateAssociationCoordinates, title]);

	// Trigger manual association search
	const triggerManualAssociationSearch = useCallback(() => {
		setShowAssociationModal(true);
	}, []);

	// Request location permission
	const requestLocationPermission = useCallback(async () => {
		try {
			const { status } = await Location.requestForegroundPermissionsAsync();
			if (status === 'granted') {
				// Refresh location after permission is granted
				window.location.reload();
			} else {
				Alert.alert('Permission Denied', 'Location permission is required to auto-detect the closest association');
			}
		} catch (error) {
			console.error('Error requesting location permission:', error);
			Alert.alert('Error', 'Failed to request location permission');
		}
	}, []);


	// Load inspection data when viewing or editing existing inspection
	React.useEffect(() => {
		if (inspectionData && (readOnly || editMode)) {
			// Load photos from inspection attachments
			const loadInspectionPhotos = async () => {
				try {
					const attachments = await dataService.getAttachmentsByInspectionReport(inspectionData.id);

					// Filter for image attachments using the isImage property
					const photoAttachments = attachments.filter(att => att.isImage);

					const inspectionPhotos: InspectionPhoto[] = photoAttachments.map((att) => {
						return {
							id: att.id,
							comment: att.description || '',
							fileName: att.fileName,
							timestamp: att.photoTakenAt ? new Date(att.photoTakenAt) : new Date(att.createdAt),
							isExisting: true, // Mark as existing photo
							attachmentId: att.id, // Store attachment ID for updates
							attachment: att, // Store the full attachment object for image loading
						};
					});

					setPhotos(inspectionPhotos);
				} catch (error) {
					console.error('Error loading inspection photos:', error);
				}
			};

			loadInspectionPhotos();
		}
	}, [readOnly, editMode, inspectionData]); // fetchApi is stable from useApi hook

	// Auto-select association if provided and auto-populate title
	React.useEffect(() => {
		if (associationId && !selectedAssociation) {
			dataService.getAssociation(associationId).then((association: Association | null) => {
				if (association) {
					setSelectedAssociation(association);
					// Auto-populate title with Association Name - Month/YYYY
					if (!title) {
						const now = new Date();
						const monthYear = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
						setTitle(`${association.name} - ${monthYear}`);
					}
				}
			});
		}
	}, [associationId, selectedAssociation, title]);

	// Auto-capture on mount: on mobile, open camera; on web, handled in PhotoCapture component
	React.useEffect(() => {
		// For initial UX, we mount PhotoCapture only once user reaches this screen; no-op here
	}, []);


	// Remove a photo
	const removePhoto = useCallback((photoId: string) => {
		setPhotos(prev => {
			const photoToRemove = prev.find(photo => photo.id === photoId);
			if (photoToRemove?.isExisting && photoToRemove?.attachmentId) {
				// Track existing photos for deletion
				setDeletedPhotoIds(prevDeleted => [...prevDeleted, photoToRemove.attachmentId || '']);
			}
			return prev.filter(photo => photo.id !== photoId);
		});
	}, []);

	// Update photo comment
	const updatePhotoComment = useCallback((photoId: string, comment: string) => {
		setPhotos(prev => prev.map(photo =>
			photo.id === photoId ? { ...photo, comment } : photo,
		));
	}, []);

	// Update existing photo comment without re-uploading
	const updateExistingPhotoComment = async (attachmentId: string, comment: string): Promise<void> => {
		try {
			await dataService.updateAttachment(attachmentId, {
				description: comment,
			});
		} catch (error) {
			console.error('Error updating photo comment:', error);
			throw error;
		}
	};

	// Delete attachments
	const deleteAttachments = async (attachmentIds: string[]): Promise<void> => {
		try {
			await Promise.all(attachmentIds.map(id => dataService.deleteAttachment(id)));
		} catch (error) {
			console.error('Error deleting attachments:', error);
			throw error;
		}
	};

	// Upload photo and create attachment
	// Simple file upload function following work order attachment pattern
	const uploadFile = async (file: { uri: string; name: string; type: string; size: number }, inspectionReportId: string): Promise<Attachment> => {

		const fields = { inspectionReportId, attachmentType: AttachmentType.Misc };
		const body = Platform.OS === 'web'
			? createTypedFormDataFromObject({ file, ...fields }).getFormData()
			: nativeUploadFormData(file, fields);

		// Upload file using fetchApi (same as work orders)
		const uploadResult = await fetchApi('/files', {
			method: 'POST',
			body,
		});

		// Create attachment record linked to inspection report
		const attachment = await dataService.createAttachment({
			fileName: uploadResult.fileName,
			fileType: 'image',
			fileSize: uploadResult.fileSize,
			storageKey: uploadResult.storageKey,
			attachmentType: AttachmentType.Misc,
			description: 'Inspection photo',
			inspectionReportId,
		});

		return attachment;
	};

	// Simple photo picker following work order attachment pattern
	const pickImage = async (useCamera = false) => {
		if (Platform.OS === 'web') {
			// Web: Use file input
			const input = document.createElement('input');
			input.type = 'file';
			input.accept = 'image/*';
			input.multiple = true;
			input.onchange = async () => {
				const files = Array.from(input.files || []);
				for (const file of files) {
					// Store the file for later upload
					setPhotos(prev => [...prev, {
						id: `temp-${Date.now()}`,
						file: {
							uri: file.name, // Use file name as URI for new files
							name: file.name,
							type: file.type,
							size: file.size,
						},
						fileName: file.name,
						comment: '',
						timestamp: new Date(),
					} as InspectionPhoto]);
				}
			};
			input.click();
			return;
		}

		try {
			let result;

			if (useCamera) {
				// Request camera permission
				const { status } = await ImagePicker.requestCameraPermissionsAsync();
				if (status !== 'granted') {
					Alert.alert('Permission required', 'Camera permission is required to take photos');
					return;
				}

				result = await ImagePicker.launchCameraAsync({
					mediaTypes: ImagePicker.MediaTypeOptions.Images,
					allowsEditing: true,
					quality: 0.8,
					preferredAssetRepresentationMode: ImagePicker.UIImagePickerPreferredAssetRepresentationMode.Compatible,
				});
			} else {
				// Request media library permission
				const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
				if (status !== 'granted') {
					Alert.alert('Permission required', 'Photo library permission is required to select photos');
					return;
				}

				result = await ImagePicker.launchImageLibraryAsync({
					mediaTypes: ImagePicker.MediaTypeOptions.Images,
					allowsEditing: true,
					quality: 0.8,
					preferredAssetRepresentationMode: ImagePicker.UIImagePickerPreferredAssetRepresentationMode.Compatible,
				});
			}

			if (result.canceled) {
				return;
			}

			const image = result.assets[0];

			// Get file info (using legacy API like work orders)
			const fileInfo = await FileSystem.getInfoAsync(image.uri);

			if (!fileInfo.exists) {
				throw new Error('File does not exist');
			}

			const fileExtension = image.uri.split('.').pop()?.toLowerCase() || 'jpg';
			const fileName = `inspection_${Date.now()}.${fileExtension}`;

			// Log file info for debugging (same as work orders)
			console.warn('File info:', fileInfo);
			console.warn('Image size:', image.width, 'x', image.height);
			console.warn('File size:', fileInfo.size, 'bytes');

			// Ensure we have a valid file size (same as work orders)
			if (!fileInfo.size || fileInfo.size <= 0) {
				console.warn('File size is invalid, estimating based on dimensions');
				// Estimate file size based on image dimensions if size is not available
				const estimatedSize = (image.width || 800) * (image.height || 600) * 0.1; // Rough estimate
				fileInfo.size = Math.max(estimatedSize, 1024); // At least 1KB
			}

			// Create file object (same as work orders)
			const file = {
				uri: image.uri,
				name: fileName,
				type: `image/${fileExtension === 'jpg' ? 'jpeg' : fileExtension}`,
				size: fileInfo.size,
			};

			console.warn('Uploading file:', file);

			// Store the file for later upload
			setPhotos(prev => [...prev, {
				id: `temp-${Date.now()}`,
				file: file,
				fileName: fileName,
				comment: '',
				timestamp: new Date(),
			} as InspectionPhoto]);
		} catch (error) {
			console.error('Error picking image:', error);
			Alert.alert('Error', 'Failed to pick image');
		}
	};

	// Create the inspection report
	const handleSaveInspection = async () => {
		if (photos.length === 0) {
			Alert.alert('No Photos', 'Please take at least one photo for the inspection');
			return;
		}

		if (!title.trim()) {
			Alert.alert('Title Required', 'Please enter a title for the inspection');
			return;
		}

		if (!associationId && !selectedAssociation) {
			Alert.alert('Association Required', 'Please select an association for the inspection');
			return;
		}

		try {
			let inspectionReport;

			if (editMode && inspectionData) {
				// Update existing inspection report
				inspectionReport = await run(() => dataService.updateInspectionReport(inspectionData.id, {
					title: title.trim(),
					associationId: associationId || selectedAssociation?.id || undefined,
					// Add location if available
					...(location && {
						latitude: location.latitude,
						longitude: location.longitude,
					}),
				}));
			} else {
				// Create new inspection report
				inspectionReport = await run(() => dataService.createInspectionReport({
					title: title.trim(),
					associationId: associationId || selectedAssociation?.id || undefined,
					userId: user?.id || '',
					// Add location if available
					...(location && {
						latitude: location.latitude,
						longitude: location.longitude,
					}),
				}));
			}

			// Handle photos based on whether they're new or existing
			const photoPromises = photos.map(async (photo) => {
				if (photo.isExisting && photo.attachmentId) {
					// Update existing photo comment only
					return updateExistingPhotoComment(photo.attachmentId, photo.comment);
				} else if (photo.file) {
					// Upload new photo using simple file upload
					return uploadFile(photo.file, inspectionReport.id);
				}
			});

			// Delete removed photos
			if (deletedPhotoIds.length > 0) {
				await deleteAttachments(deletedPhotoIds);
			}

			await Promise.all(photoPromises);

			// Clear deleted photos tracking
			setDeletedPhotoIds([]);

			if (onSuccess) {
				onSuccess(inspectionReport);
			}

			// Add delay and refetch all data after creating new inspection
			if (!editMode) {
				setTimeout(() => {
					refetchAll();
				}, 1000);
				router.replace(`/inspections/${inspectionReport.id}`);
			}
		} catch (error) {
			console.error(`Error ${editMode ? 'updating' : 'creating'} inspection:`, error);
			Alert.alert('Error', editMode ? 'Failed to update inspection report' : 'Failed to create inspection report');
		}
	};

	return (
		<KeyboardAwareScrollView style={{ flex: 1, padding: 16 }} extraScrollHeight={100}>
			<Text variant="headlineSmall" style={{ marginBottom: 16 }}>
				{editMode ? 'Edit Inspection Report' : readOnly ? 'Inspection Report' : 'New Inspection Report'}
			</Text>

			{/* Basic Information */}
			<Card style={{ marginBottom: 16, padding: 16 }}>
				{/* Association Selection */}
				<View style={{ marginBottom: 12 }}>
					<Text variant="titleMedium" style={{ marginBottom: 8 }}>
						Association
					</Text>

					{selectedAssociation ? (
						<View style={{
							flexDirection: 'row',
							alignItems: 'center',
							backgroundColor: theme.colors.surfaceVariant,
							padding: 12,
							borderRadius: 8,
							marginBottom: 8,
						}}>
							<Ionicons name="business" size={20} color={theme.colors.primary} />
							<View style={{ marginLeft: 12, flex: 1 }}>
								<Text variant="bodyMedium" style={{ fontWeight: '600' }}>
									{selectedAssociation.name}
								</Text>
								<Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
									{selectedAssociation.address}, {selectedAssociation.city}
								</Text>
								{!isReadOnly && closestAssociation && !manualAssociation && (
									<Text variant="bodySmall" style={{ color: theme.colors.primary }}>
										{closestAssociation.distance.toFixed(1)} miles away (auto-detected)
									</Text>
								)}
								{!isReadOnly && manualAssociation && (
									<Text variant="bodySmall" style={{ color: theme.colors.primary }}>
										Manually selected
									</Text>
								)}
								{!isReadOnly && !selectedAssociation.latitude && !selectedAssociation.longitude && location && (
									<Text variant="bodySmall" style={{ color: theme.colors.secondary }}>
										Location coordinates will be added
									</Text>
								)}
							</View>
							{!isReadOnly && (
								<Button
									mode="text"
									onPress={triggerManualAssociationSearch}
									style={{ marginLeft: 8 }}
								>
									Change
								</Button>
							)}
						</View>
					) : (
						<View style={{
							backgroundColor: theme.colors.surfaceVariant,
							padding: 12,
							borderRadius: 8,
							marginBottom: 8,
						}}>
							<Text variant="bodyMedium" style={{ marginBottom: 8 }}>
								No Association Selected
							</Text>
							<Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant, marginBottom: 8 }}>
								{!hasLocationPermission
									? 'Location permission is required to auto-detect the closest association'
									: loading
										? 'Detecting location...'
										: error
											? 'Location detection failed'
											: 'No associations found nearby'
								}
							</Text>
							<View style={{ flexDirection: 'row', gap: 8 }}>
								{!hasLocationPermission && (
									<Button
										mode="outlined"
										onPress={requestLocationPermission}
										style={{ flex: 1 }}
									>
										Enable Location
									</Button>
								)}
								<Button
									mode="contained"
									onPress={triggerManualAssociationSearch}
									style={{ flex: 1 }}
								>
									Select Manually
								</Button>
							</View>
						</View>
					)}

				</View>


				<TextInput
					label="Title *"
					value={title}
					onChangeText={setTitle}
					placeholder="Enter inspection title"
					style={{ marginBottom: 12 }}
					disabled={isReadOnly}
					readOnly={isReadOnly}
				/>
			</Card>

			{/* Photo Capture Section - emphasized at top */}
			<Card style={{ marginBottom: 16, padding: 16 }}>
				<Text variant="titleMedium" style={{ marginBottom: 12 }}>
					Photos ({photos.length})
				</Text>

				{photos.length === 0 ? (
					<View style={{
						alignItems: 'center',
						padding: 32,
						backgroundColor: theme.colors.surfaceVariant,
						borderRadius: 8,
						borderStyle: 'dashed',
						borderWidth: 2,
						borderColor: theme.colors.outline,
					}}>
						<Ionicons name="camera-outline" size={48} color={theme.colors.outline} />
						<Text variant="bodyMedium" style={{ marginTop: 8, textAlign: 'center' }}>
							Start by taking photos of the inspection area
						</Text>
					</View>
				) : (
					<View style={{ gap: 12 }}>
						{photos.map((photo, index) => (
							<View key={photo.id} style={{
								borderWidth: 1,
								borderColor: theme.colors.outline,
								borderRadius: 8,
								overflow: 'hidden',
							}}>
								<View style={{ position: 'relative' }}>
									<View style={{ width: '100%', height: 200 }}>
										<InspectionPhotoImage photo={photo} />
									</View>
									{editMode && (
										<TouchableOpacity
											style={{
												position: 'absolute',
												top: 8,
												right: 8,
												backgroundColor: 'rgba(0,0,0,0.6)',
												borderRadius: 16,
												width: 32,
												height: 32,
												justifyContent: 'center',
												alignItems: 'center',
											}}
											onPress={() => removePhoto(photo.id)}
										>
											<Ionicons name="close" size={20} color="white" />
										</TouchableOpacity>
									)}
									<View style={{
										position: 'absolute',
										bottom: 8,
										left: 8,
										backgroundColor: 'rgba(0,0,0,0.6)',
										paddingHorizontal: 8,
										paddingVertical: 4,
										borderRadius: 4,
									}}>
										<Text style={{ color: 'white', fontSize: 12 }}>
											Photo {index + 1}
										</Text>
									</View>
									<View style={{
										position: 'absolute',
										top: 8,
										left: 8,
										backgroundColor: 'rgba(0,0,0,0.6)',
										paddingHorizontal: 8,
										paddingVertical: 4,
										borderRadius: 4,
									}}>
										<Text style={{ color: 'white', fontSize: 10 }}>
											{photo.timestamp.toLocaleString()}
										</Text>
									</View>
									{editMode && photo.isExisting && (
										<View style={{
											position: 'absolute',
											bottom: 8,
											right: 8,
											backgroundColor: 'rgba(0,150,0,0.8)',
											paddingHorizontal: 6,
											paddingVertical: 2,
											borderRadius: 4,
										}}>
											<Text style={{ color: 'white', fontSize: 8 }}>
												EXISTING
											</Text>
										</View>
									)}
								</View>

								<View style={{ padding: 12 }}>
									<TextInput
										label={`Comment for Photo ${index + 1}`}
										value={photo.comment}
										onChangeText={(text) => updatePhotoComment(photo.id, text)}
										placeholder="Add a comment about this photo (optional)"
										multiline
										numberOfLines={2}
										disabled={isReadOnly}
										readOnly={isReadOnly}
									/>
								</View>
							</View>
						))}
					</View>
				)}

				{/* Always show Add Photos buttons */}
				{!isReadOnly && (
					<View style={{ flexDirection: 'row', gap: 12, marginTop: 16 }}>
						<Button
							mode="outlined"
							onPress={() => pickImage(false)}
							icon="image"
							style={{ flex: 1 }}
						>
							{Platform.OS === 'web' ? 'Add Photos' : 'Add from Gallery'}
						</Button>
						{Platform.OS !== 'web' && (
							<Button
								mode="outlined"
								onPress={() => pickImage(true)}
								icon="camera"
								style={{ flex: 1 }}
							>
								Take Photo
							</Button>
						)}
					</View>
				)}
			</Card>

			{/* Action Buttons */}
			{!isReadOnly && (
				<View style={{ flexDirection: 'row', gap: 12, marginBottom: 16 }}>
					<Button
						mode="outlined"
						onPress={() => router.replace('/')}
						style={{ flex: 1 }}
					>
						Cancel
					</Button>
					<Button
						mode="contained"
						onPress={handleSaveInspection}
						loading={isSubmitting}
						disabled={isSubmitting || photos.length === 0 || !title.trim() || (!associationId && !selectedAssociation)}
						style={{ flex: 1 }}
					>
						{editMode ? 'Update Inspection' : 'Create Inspection'}
					</Button>
				</View>
			)}

			{/* Association Search Modal */}
			<AssociationsSearchModal
				visible={showAssociationModal}
				onDismiss={() => setShowAssociationModal(false)}
				onSelect={handleManualAssociationSelect}
				placeholder="Search associations..."
				selectedAssociation={selectedAssociation}
			/>
		</KeyboardAwareScrollView>
	);
};
